/**
 * Finance Service API client (JWT + Photon headers).
 */
import { useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";

import { useDevBypass } from "../utils/devBypass";
import { useTokenManager } from "./useTokenManager";
import { getConfig } from "../config";

interface FinanceApiClient {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  put: <T>(path: string, body?: unknown) => Promise<T>;
  getAuthHeaders: (forceRefresh?: boolean) => Promise<HeadersInit>;
  getBaseUrl: () => string;
  isConfigured: () => boolean;
}

export function useFinanceApiClient(): FinanceApiClient {
  const { instance } = useMsal();
  const tokenManager = useTokenManager();

  const getAuthHeaders = useCallback(
    async (forceRefresh = false): Promise<HeadersInit> => {
      let token = await tokenManager.getToken(forceRefresh);
      if (!token) {
        token = await tokenManager.getToken(true);
      }
      if (!token) {
        throw new Error("Session expired. Please refresh the page and try again.");
      }
      const { userId, email, roles } = tokenManager.getUserInfo();
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-User-Id": userId,
        "X-User-Email": email,
        "X-User-Roles": roles.join(","),
      };
    },
    [tokenManager],
  );

  const fetchWithAuth = useCallback(
    async <T>(path: string, options: RequestInit = {}): Promise<T> => {
      const headers = await getAuthHeaders();
      const base = getConfig().financeServiceUrl || "";
      const url = `${base}${path}`;
      let response = await fetch(url, {
        ...options,
        headers: { ...headers, ...options.headers } as HeadersInit,
      });

      if (response.status === 401 && !useDevBypass) {
        const retryHeaders = await getAuthHeaders(true);
        response = await fetch(url, {
          ...options,
          headers: { ...retryHeaders, ...options.headers } as HeadersInit,
        });

        if (response.status === 401) {
          try {
            await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] });
          } catch {
            /* redirect navigates away */
          }
          throw new Error("Session expired. Redirecting to login...");
        }

        if (!response.ok) {
          const text = await response.text().catch(() => response.statusText);
          throw new Error(`HTTP ${response.status}: ${text}`);
        }
        return response.status === 204 ? ({} as T) : response.json();
      }

      if (!response.ok) {
        const text = await response.text().catch(() => response.statusText);
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      return response.status === 204 ? ({} as T) : response.json();
    },
    [getAuthHeaders, instance],
  );

  return useMemo<FinanceApiClient>(
    () => ({
      get: <T>(path: string) => fetchWithAuth<T>(path),
      post: <T>(path: string, body?: unknown) =>
        fetchWithAuth<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
      put: <T>(path: string, body?: unknown) =>
        fetchWithAuth<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
      getAuthHeaders,
      getBaseUrl: () => getConfig().financeServiceUrl || "",
      isConfigured: () => !!getConfig().financeServiceUrl,
    }),
    [fetchWithAuth, getAuthHeaders],
  );
}

export default useFinanceApiClient;
